import React, { useState } from 'react';
import { X, Search, Building2, FileText, HelpCircle, CreditCard } from 'lucide-react';
import { Email, SystemRecord, ReferenceType } from '../types/email';
import { mockSystemRecords } from '../data/mockData';
interface ManualLinkModalProps {
  email: Email;
  onClose: () => void;
  onLink: (recordIds: string[]) => void;
}
const typeIcons = {
  company: Building2,
  case: FileText,
  enquiry: HelpCircle,
  transaction: CreditCard
};
export function ManualLinkModal({
  email,
  onClose,
  onLink
}: ManualLinkModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<ReferenceType | 'all'>('all');
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set());
  const filteredRecords = mockSystemRecords.filter(record => {
    const matchesType = selectedType === 'all' || record.type === selectedType;
    const matchesSearch = searchQuery === '' || record.name.toLowerCase().includes(searchQuery.toLowerCase()) || record.referenceNumber.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });
  const toggleRecord = (recordId: string) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };
  const handleLink = () => {
    onLink(Array.from(selectedRecords));
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-45">
      <div className="bg-white rounded shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col" data-id="element-46">
        {/* Teal Header */}
        <div className="px-6 py-4" style={{
        backgroundColor: 'var(--teal-section)'
      }} data-id="element-47">
          <div className="flex items-center justify-between" data-id="element-48">
            <div data-id="element-49">
              <h2 className="text-xl font-semibold text-white" data-id="element-50">
                Link Email to Records
              </h2>
              <p className="text-sm text-white/80 mt-1" data-id="element-51">
                Select one or more records to link to this email
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded transition-colors" data-id="element-52">
              <X className="w-5 h-5 text-white" data-id="element-53" />
            </button>
          </div>
        </div>

        <div className="p-6 border-b" style={{
        borderColor: 'var(--border-light)'
      }} data-id="element-54">
          <div className="flex gap-3 mb-4" data-id="element-55">
            <div className="flex-1 relative" data-id="element-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-57" />
              <input type="text" placeholder="Search by name or reference number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400" style={{
              borderColor: 'var(--border-light)'
            }} data-id="element-58" />
            </div>
          </div>

          <div className="flex gap-2" data-id="element-59">
            {(['all', 'company', 'case', 'enquiry', 'transaction'] as const).map(type => <button key={type} onClick={() => setSelectedType(type)} className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${selectedType === type ? 'text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} style={selectedType === type ? {
            backgroundColor: 'var(--teal-section)'
          } : {}} data-id="element-60">
                {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
              </button>)}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6" data-id="element-61">
          <div className="space-y-2" data-id="element-62">
            {filteredRecords.map(record => {
            const Icon = typeIcons[record.type];
            const isSelected = selectedRecords.has(record.id);
            return <div key={record.id} onClick={() => toggleRecord(record.id)} className={`flex items-center gap-3 p-4 border rounded cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`} style={!isSelected ? {
              borderColor: 'var(--border-light)'
            } : {}} data-id="element-63">
                  <input type="checkbox" checked={isSelected} onChange={() => {}} className="w-4 h-4 rounded" style={{
                accentColor: 'var(--teal-section)'
              }} data-id="element-64" />
                  <div className="p-2 bg-white rounded border" style={{
                borderColor: 'var(--border-light)'
              }} data-id="element-65">
                    <Icon className="w-4 h-4 text-gray-600" data-id="element-66" />
                  </div>
                  <div className="flex-1" data-id="element-67">
                    <div className="flex items-center gap-2 mb-1" data-id="element-68">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-69">
                        {record.type}
                      </span>
                      <span className="text-xs font-mono text-gray-400" data-id="element-70">
                        {record.referenceNumber}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900" data-id="element-71">
                      {record.name}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5" data-id="element-72">
                      Status: {record.status}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t" style={{
        borderColor: 'var(--border-light)',
        backgroundColor: '#fafbfc'
      }} data-id="element-73">
          <p className="text-sm text-gray-600" data-id="element-74">
            {selectedRecords.size} record{selectedRecords.size !== 1 ? 's' : ''}{' '}
            selected
          </p>
          <div className="flex gap-3" data-id="element-75">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border rounded hover:bg-gray-100 transition-colors" style={{
            borderColor: 'var(--border-light)'
          }} data-id="element-76">
              Cancel
            </button>
            <button onClick={handleLink} disabled={selectedRecords.size === 0} className="px-4 py-2 text-white text-sm font-medium rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity" style={{
            backgroundColor: 'var(--teal-section)'
          }} data-id="element-77">
              Link {selectedRecords.size > 0 && `(${selectedRecords.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>;
}